// The two things that live INSIDE a member field rather than beside it.
//
// coact's Input already renders `props.endAdornment` (inputs/Input.jsx), and
// buildFieldProps spreads a ui field entry through untouched, so anything put
// on a field in pageConfig arrives here. That is the whole trick: no custom
// field component, no fork of FormEditor.
//
// Both read or write the document through redux directly, because a field's
// Editor context is scoped to that field's own path -- from inside
// `spendPoints.value` there is no way back up to `consumer.members`.

import PropTypes from 'prop-types'

import { connect } from 'react-redux'

import * as $editor from 'coact/store/editor'
import createEffectAction from 'coact/pages/modules/pageAction/createEffectAction'

import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import RefreshIcon from '@mui/icons-material/Refresh'

import { balanceOf } from './memberBalance.js'

const { func, number, oneOfType, string } = PropTypes

// `/500` at the right-hand end of a spend field: what there is to spend, next
// to the box where the staff says how much of it to use.
//
// A dash when the platform reported nothing. NOT `/0` -- a cashier who reads
// "0" tells the customer they have nothing, which is a different statement from
// "we could not find out".
function BalanceSuffixView(props) {
  const { balance } = props

  const text =
    balance === null || balance === undefined
      ? '—'
      : Number(balance).toLocaleString('zh-TW')

  return (
    <InputAdornment position="end">
      <span style={{ whiteSpace: 'nowrap', color: 'rgba(0, 0, 0, 0.54)' }}>
        {`/${text}`}
      </span>
    </InputAdornment>
  )
}
BalanceSuffixView.propTypes = {
  balance: oneOfType([number, string]),
}

// KEYED BY VENDOR, not by position. The membership array has one row per
// platform in whatever order they were resolved, so `members[0]` would put
// another vendor's balance beside this vendor's 折抵 box.
const BalanceSuffix = connect((state, { vendor, field }) => ({
  balance: balanceOf($editor.select.value(state), vendor, field),
}))(BalanceSuffixView)

BalanceSuffix.propTypes = {
  vendor: string.isRequired,
  field: string.isRequired,
}

// Re-ask the platform what the member has now. The balance is a snapshot taken
// when the phone was typed, and a customer who has just spent points online is
// exactly the case where the staff needs to ask again.
//
// It rewrites the whole snapshot row, which is safe because nothing reads the
// snapshot back to decide anything. Awards already on the order froze their own
// vendor and vendorId when they were created, so re-resolving a phone that has
// since come to match someone else cannot move them.
function ReloadBalancesView(props) {
  const { dispatch } = props

  return (
    <InputAdornment position="end">
      <IconButton
        title="重新查詢點數／購物金"
        aria-label="重新查詢點數／購物金"
        onClick={() =>
          dispatch(createEffectAction({ name: 'reloadMemberBalances' }))
        }
        style={{ width: 28, height: 28, padding: 0 }}
      >
        <RefreshIcon style={{ fontSize: 18 }} />
      </IconButton>
    </InputAdornment>
  )
}
ReloadBalancesView.propTypes = {
  dispatch: func.isRequired,
}

const ReloadBalances = connect()(ReloadBalancesView)

// Ready-made elements, so pageConfig stays a config file with no JSX in it.
// One per vendor, because a vendor section owns its own balances.
export const pointsBalance = vendor => (
  <BalanceSuffix vendor={vendor} field="points" />
)
export const creditBalance = vendor => (
  <BalanceSuffix vendor={vendor} field="credits" />
)
export const reloadBalances = <ReloadBalances />
